/**
 * Mobile cookie banner: dismiss state, accept all → consent, open policy / settings drawers,
 * YSL-style opacity fade on close.
 */
import {
  saveConsentState,
  getAllowAllState,
  PrivacyDrawerOpenEvent,
} from './privacy-preference-drawer.js';

const STORAGE_KEY = 'tds-cookie-banner-v1';
const FADE_MS = 450;

function isDesignMode() {
  return typeof window.Shopify !== 'undefined' && window.Shopify.designMode;
}

function shouldShow() {
  if (isDesignMode()) return true;
  try {
    return !localStorage.getItem(STORAGE_KEY);
  } catch {
    return true;
  }
}

function dismissBanner(root) {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* ignore */
  }

  if (root.classList.contains('cookie-banner--is-closing')) return;

  let finished = false;
  const complete = () => {
    if (finished) return;
    finished = true;
    root.hidden = true;
    root.classList.remove('cookie-banner--is-closing');
  };

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    complete();
    return;
  }

  const onTransitionEnd = (e) => {
    if (e.target !== root || e.propertyName !== 'opacity') return;
    root.removeEventListener('transitionend', onTransitionEnd);
    complete();
  };

  root.addEventListener('transitionend', onTransitionEnd);
  root.classList.add('cookie-banner--is-closing');

  setTimeout(() => {
    root.removeEventListener('transitionend', onTransitionEnd);
    complete();
  }, FADE_MS);
}

function openCookiePolicy() {
  const el = document.getElementById('footer-cookie-drawer');
  if (el && typeof el.showDialog === 'function') {
    el.showDialog();
    return;
  }
  const dialog = document.querySelector('#footer-cookie-drawer dialog');
  if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
}

/**
 * Cookie settings → privacy preference center (same as footer “cookie settings” intent).
 */
function openCookieSettings() {
  const tryOpen = () => {
    const drawer = document.querySelector('privacy-preference-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    if (typeof window.openPrivacyPreferenceCenter === 'function') {
      window.openPrivacyPreferenceCenter();
      return true;
    }
    if (typeof window.openDrawer === 'function') {
      window.openDrawer('cookie-settings');
      return true;
    }
    return false;
  };

  if (tryOpen()) return;

  requestAnimationFrame(() => {
    if (tryOpen()) return;
    requestAnimationFrame(() => {
      if (tryOpen()) return;
      document.dispatchEvent(new PrivacyDrawerOpenEvent());
    });
  });
}

function init() {
  const root = document.querySelector('[data-cookie-banner]');
  if (!root) return;

  const accept = root.querySelector('[data-cookie-banner-accept]');
  const settings = root.querySelector('[data-cookie-banner-settings]');
  const policy = root.querySelector('[data-cookie-banner-policy]');

  if (!shouldShow()) {
    root.hidden = true;
    root.classList.remove('cookie-banner--is-closing');
    return;
  }

  root.classList.remove('cookie-banner--is-closing');
  root.hidden = false;

  accept?.addEventListener('click', () => {
    saveConsentState(getAllowAllState());
    dismissBanner(root);
  });

  settings?.addEventListener('click', () => {
    openCookieSettings();
  });

  policy?.addEventListener('click', () => {
    openCookiePolicy();
  });

  document.addEventListener('privacy:confirmed', () => dismissBanner(root));
  document.addEventListener('privacy:rejected', () => dismissBanner(root));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
